import { useEffect, useMemo, useRef, useState } from "react"
import cx from "clsx"
import { useQuery } from "@tanstack/react-query"
import { draggable } from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import type { TemplateModule } from "shared"
import { fetchPublishedFormModules } from "../api"
import { libraryModule } from "../utils/templateDrag"
import LayersIcon from "../../../assets/icon-layers.svg?react"
import PlusIcon from "@rawa/ui/assets/icon-plus.svg?react"
import styles from "./ModuleLibrary.module.css"

/**
 * The modules a session template can be built from, beside the canvas.
 *
 * Each one can be added to the template through `onAdd`. Without it there is
 * nowhere to add a module to, so the library only says what exists. It fetches
 * for itself rather than being handed a list, so the builder page does not grow
 * another load. It shows each module as published, the version a template
 * adds — never a working copy, which only the module's own screens show
 * (ADR-040).
 */
export function ModuleLibrary({
  onAdd,
  placedIds = [],
}: {
  onAdd?: (module: TemplateModule) => void
  /** The modules already on the template, which have left the library. */
  placedIds?: string[]
}) {
  const [search, setSearch] = useState("")

  // Read each time the library is shown, since another administrator may have
  // published a module meanwhile, and never in the background.
  const {
    data: modules = [],
    isPending,
    isError,
  } = useQuery({
    queryKey: ["form-modules", "published"],
    queryFn: ({ signal }) => fetchPublishedFormModules(signal),
    staleTime: Infinity,
    refetchOnMount: "always",
    // The client's default retries exist for a kiosk tablet waking its Wi-Fi.
    // An administrator at a desk is told what went wrong instead.
    retry: false,
    meta: { source: "fetchPublishedFormModules" },
  })

  // A placed module leaves the library: adding it twice is not a choice, so it
  // is not offered. Matched on description too, because a module's name is
  // often shorter than the thing a person remembers about it.
  const offered = useMemo(
    () => modules.filter((m) => !placedIds.includes(m.id)),
    [modules, placedIds],
  )
  const matches = useMemo(() => {
    const wanted = search.trim().toLowerCase()
    return offered.filter((m) =>
      `${m.name} ${m.description}`.toLowerCase().includes(wanted),
    )
  }, [offered, search])

  return (
    <aside aria-label="Module library" className={styles.library}>
      <p className={styles.eyebrow}>Modules</p>
      <h2 className={styles.heading}>Module library</h2>
      <p className={styles.help}>
        The modules a template can be built from. Modules are built on the
        Modules screen.
      </p>

      <input
        type="search"
        className={styles.search}
        aria-label="Search modules"
        placeholder="Search by name"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {isPending ? (
        <div role="status" aria-label="Loading modules" className={styles.list}>
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.item}>
              <span className={styles.skeletonBody}>
                <span className={styles.skeletonName} />
                <span className={styles.skeletonLabel} />
              </span>
            </div>
          ))}
        </div>
      ) : isError ? (
        // A template can be set up without this list, so a failure says so
        // here rather than taking the page down.
        <p className={styles.empty}>Could not load the module library.</p>
      ) : matches.length === 0 ? (
        <p className={styles.empty}>
          {modules.length === 0
            ? "No modules published yet."
            : offered.length === 0
              ? "Every module is on the template."
              : "No matches."}
        </p>
      ) : (
        <ul className={styles.list}>
          {matches.map((module) => (
            <LibraryItem key={module.id} module={module} onAdd={onAdd} />
          ))}
        </ul>
      )}
    </aside>
  )
}

/** A module in the library. Where it can be added, it can also be dragged. */
function LibraryItem({
  module,
  onAdd,
}: {
  module: TemplateModule
  onAdd?: (module: TemplateModule) => void
}) {
  const ref = useRef<HTMLLIElement>(null)
  const addable = onAdd !== undefined

  useEffect(() => {
    if (!addable) return
    return draggable({
      element: ref.current!,
      getInitialData: () => libraryModule.data(module),
    })
  }, [module, addable])

  return (
    <li ref={ref} className={cx(styles.item, onAdd && styles.interactive)}>
      {/* The whole card adds the module, not only its plus — the plus stays
          as the affordance, decorative either way. */}
      {onAdd ? (
        <button
          type="button"
          className={styles.addCard}
          aria-label={`Add ${module.name}`}
          onClick={() => onAdd(module)}
        >
          <ModuleCard module={module} />
        </button>
      ) : (
        <ModuleCard module={module} />
      )}
    </li>
  )
}

/** What a library card shows: the module's name, its entity link, the plus. */
function ModuleCard({ module }: { module: TemplateModule }) {
  return (
    <>
      <LayersIcon className={styles.itemIcon} aria-hidden="true" />
      <span className={styles.itemBody}>
        <strong>{module.name}</strong>
        <small>{module.entityLinkLabel}</small>
      </span>
      <PlusIcon className={styles.itemAdd} aria-hidden="true" />
    </>
  )
}
